"use client"

import { useEffect } from "react"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useForm, Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { register as registerUser, resetAuthState } from "../slices/authSlice"
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Avatar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Email,
  Lock,
  Person,
  Phone,
  Percent,
  Security,
  Chat
} from "@mui/icons-material"

// Styled components
const GreenSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#008069",
  color: "white",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(6),
  height: "100vh",
}))

const FormSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  height: "100%",
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  overflowY: "auto",
}))

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 600,
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}))

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  padding: theme.spacing(2),
  marginRight: theme.spacing(2),
  width: 60,
  height: 60,
}))

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  backgroundColor: "#008069",
  "&:hover": {
    backgroundColor: "#128C7E",
  },
}))

const RegisterPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loading, error, success } = useSelector((state) => state.auth)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      userName: "",
      email: "",
      password: "",
      mobile: "",
      cuttingPercentage: "",
      role: "user",
    },
  })

  useEffect(() => {
    if (success) {
      toast.success("Registration successful! Please log in.")
      navigate("/login?registered=true")
      dispatch(resetAuthState())
    }
  }, [success, navigate, dispatch])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetAuthState())
    }
  }, [error, dispatch])

  const onSubmit = (data) => {
    dispatch(registerUser(data))
  }

  return (
    <Grid container component="main" sx={{ minHeight: "100vh" }}>
      {/* Left side - Green background with logo */}
      <Grid item xs={false} sm={false} md={6}>
        <GreenSection>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <LogoAvatar>
              <Chat fontSize="large" />
            </LogoAvatar>
            <Typography variant="h3" component="h1" fontWeight="bold">
              WhatsApp Campaign
            </Typography>
          </Box>
          <Typography variant="h6" align="center" sx={{ maxWidth: 450 }}>
            Manage your WhatsApp marketing campaigns efficiently and reach your audience effectively.
          </Typography>
        </GreenSection>
      </Grid>

      {/* Right side - Registration form */}
      <Grid item xs={12} sm={12} md={6}>
        <FormSection>
          <FormPaper elevation={2}>
            <Typography variant="h4" component="h2" align="center" fontWeight="bold" gutterBottom>
              Create Account
            </Typography>
            <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
              Enter your information to get started
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="name"
                        label="Full Name"
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="userName"
                    control={control}
                    rules={{ required: "Username is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="userName"
                        label="Username"
                        variant="outlined"
                        error={!!errors.userName}
                        helperText={errors.userName?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="email"
                        label="Email Address"
                        variant="outlined"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="password"
                        label="Password"
                        type="password"
                        variant="outlined"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="mobile"
                    control={control}
                    rules={{ required: "Mobile number is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="mobile"
                        label="Mobile"
                        variant="outlined"
                        error={!!errors.mobile}
                        helperText={errors.mobile?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="cuttingPercentage"
                    control={control}
                    rules={{ required: "Cutting percentage is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        id="cuttingPercentage"
                        label="Cutting Percentage"
                        variant="outlined"
                        error={!!errors.cuttingPercentage}
                        helperText={errors.cuttingPercentage?.message}
                        disabled={loading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Percent color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <FormControl 
                        fullWidth 
                        variant="outlined" 
                        error={!!errors.role}
                        sx={{ 
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "rgba(232, 240, 254, 0.5)"
                          }
                        }}
                      >
                        <InputLabel id="role-label">Account Type</InputLabel>
                        <Select
                          {...field}
                          labelId="role-label"
                          id="role"
                          label="Account Type"
                          disabled={loading}
                          startAdornment={
                            <InputAdornment position="start">
                              <Security color="action" />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="reseller">Reseller</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                        {errors.role && <FormHelperText>{errors.role.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>

              <SubmitButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1, color: "white" }} />
                    Creating account...
                  </>
                ) : (
                  "Sign up"
                )}
              </SubmitButton>

              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  Already have an account?{" "}
                  <Link component={RouterLink} to="/login" sx={{ color: "#008069", fontWeight: 500 }}>
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </form>
          </FormPaper>
        </FormSection>
      </Grid>
    </Grid>
  )
}

export default RegisterPage
